<template>
  <div class="roles-page">
    <!-- Page Header with Stats Summary -->
    <div class="page-header mb-5">
      <div class="columns is-multiline">
        <div class="column is-8">
          <div class="content">
            <h1 class="title is-3 mb-2">
              <span class="icon-text">
                <span class="icon is-medium has-text-primary mr-2">
                  <i class="bx bx-shield-quarter bx-sm"></i>
                </span>
                <span>Roles Management</span>
              </span>
            </h1>
            <p class="subtitle is-6 has-text-grey mt-1">
              Manage user roles and their associated permissions for system access control
            </p>
          </div>
        </div>
        <div class="column is-4">
          <div class="buttons is-right">
            <permission-router-link 
              to="/roles/create" 
              class="button is-primary is-outlined"
            >
              <span class="icon">
                <i class="bx bx-plus"></i>
              </span>
              <span>Add New Role</span>
            </permission-router-link>
            <button class="button is-light" @click="fetchRoles" title="Refresh roles list">
              <span class="icon">
                <i class="bx bx-refresh"></i>
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Role Stats Cards -->
      <div class="columns is-multiline mt-3" v-if="!loading && roles.length > 0">
        <div class="column is-3">
          <div class="box stat-box has-background-primary-light">
            <div class="level is-mobile mb-1">
              <div class="level-left">
                <div>
                  <p class="heading has-text-primary-dark">Total Roles</p>
                  <p class="title is-4">{{ roles.length }}</p>
                </div>
              </div>
              <div class="level-right">
                <span class="icon is-large has-text-primary">
                  <i class="bx bx-group bx-sm"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="box stat-box has-background-info-light">
            <div class="level is-mobile mb-1">
              <div class="level-left">
                <div>
                  <p class="heading has-text-info-dark">Total Permissions</p>
                  <p class="title is-4">{{ getTotalPermissions() }}</p>
                </div>
              </div>
              <div class="level-right">
                <span class="icon is-large has-text-info">
                  <i class="bx bx-key bx-sm"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="box stat-box has-background-success-light">
            <div class="level is-mobile mb-1">
              <div class="level-left">
                <div>
                  <p class="heading has-text-success-dark">Admin Roles</p>
                  <p class="title is-4">{{ getAdminRoles() }}</p>
                </div>
              </div>
              <div class="level-right">
                <span class="icon is-large has-text-success">
                  <i class="bx bx-crown bx-sm"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="box stat-box has-background-warning-light">
            <div class="level is-mobile mb-1">
              <div class="level-left">
                <div>
                  <p class="heading has-text-warning-dark">Latest Role</p>
                  <p class="title is-5 truncate-text">{{ getLatestRole() }}</p>
                </div>
              </div>
              <div class="level-right">
                <span class="icon is-large has-text-warning">
                  <i class="bx bx-time bx-sm"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="notification is-success is-light fade-out">
      <button class="delete" @click="successMessage = ''"></button>
      {{ successMessage }}
    </div>

    <!-- Main Content Card -->
    <div class="card main-card">
      <header class="card-header has-background-primary">
        <p class="card-header-title has-text-white">
          <span class="icon mr-2">
            <i class="bx bx-list-ul"></i>
          </span>
          Roles List
        </p>
        <!-- Search Field -->
        <div class="card-header-icon" v-if="!loading && roles.length > 0">
          <div class="field has-addons mb-0">
            <div class="control has-icons-left">
              <input 
                class="input is-small" 
                type="text" 
                placeholder="Search roles..." 
                v-model="searchQuery"
              >
              <span class="icon is-small is-left">
                <i class="bx bx-search"></i>
              </span>
            </div>
            <div class="control" v-if="searchQuery">
              <button class="button is-small is-light" @click="searchQuery = ''">
                <span class="icon is-small">
                  <i class="bx bx-x"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div class="card-content">
        <!-- Table View -->
        <div class="table-container" v-if="!loading && filteredRoles.length > 0">
          <table class="table is-fullwidth is-striped is-hoverable roles-table">
            <thead>
              <tr>
                <th class="is-narrow">
                  <span class="icon is-small has-text-primary">
                    <i class="bx bx-id-card"></i>
                  </span>
                </th>
                <th @click="sortBy('name')" class="is-sortable">
                  Name
                  <span class="icon is-small" v-if="sortColumn === 'name'">
                    <i class="bx" :class="sortDirection === 'asc' ? 'bx-sort-up' : 'bx-sort-down'"></i>
                  </span>
                </th>
                <th>Description</th>
                <th @click="sortBy('permissions_count')" class="is-sortable">
                  Permissions
                  <span class="icon is-small" v-if="sortColumn === 'permissions_count'">
                    <i class="bx" :class="sortDirection === 'asc' ? 'bx-sort-up' : 'bx-sort-down'"></i>
                  </span>
                </th>
                <th @click="sortBy('created_at')" class="is-sortable">
                  Created
                  <span class="icon is-small" v-if="sortColumn === 'created_at'">
                    <i class="bx" :class="sortDirection === 'asc' ? 'bx-sort-up' : 'bx-sort-down'"></i>
                  </span>
                </th>
                <th class="has-text-centered">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="role in filteredRoles" :key="role.id" :class="{'has-background-primary-light': role.name.toLowerCase().includes('admin')}">
                <td class="is-narrow">
                  <span class="icon has-text-primary" v-if="role.name.toLowerCase().includes('admin')">
                    <i class="bx bx-crown"></i>
                  </span>
                  <span class="icon" v-else>
                    <i class="bx bx-shield-quarter"></i>
                  </span>
                </td>
                <td>
                  <div class="role-name">
                    <permission-router-link 
                      :to="`/roles/${role.id}/edit`" 
                      class="has-text-weight-bold has-text-dark"
                      showAlways
                    >{{ role.name }}</permission-router-link>
                  </div>
                </td>
                <td>
                  <p class="description-cell">{{ role.description || 'No description provided' }}</p>
                </td>
                <td>
                  <span class="tag is-info is-light">
                    <span class="icon is-small mr-1">
                      <i class="bx bx-key"></i>
                    </span>
                    {{ role.permissions_count || 0 }} Permissions
                  </span>
                </td>
                <td>
                  <span class="has-text-grey" :title="formatDateFull(role.created_at)">
                    {{ formatDate(role.created_at) }}
                  </span>
                </td>
                <td>
                  <div class="buttons is-centered are-small">
                    <permission-router-link 
                      :to="`/roles/${role.id}/edit`" 
                      class="button is-primary is-outlined" 
                      title="Edit role"
                    >
                      <span class="icon">
                        <i class="bx bx-edit"></i>
                      </span>
                    </permission-router-link>

                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- No Results View -->
        <div v-else-if="!loading && searchQuery && roles.length > 0" class="has-text-centered py-5">
          <div class="is-flex is-justify-content-center mb-4">
            <span class="icon is-large has-text-grey-light">
              <i class="bx bx-search-alt bx-lg"></i>
            </span>
          </div>
          <h4 class="title is-5 mb-3">No Matching Roles</h4>
          <p class="has-text-grey mb-4">No roles match your search for "{{ searchQuery }}"</p>
          <div>
            <button @click="searchQuery = ''" class="button is-primary is-light">
              <span class="icon">
                <i class="bx bx-reset"></i>
              </span>
              <span>Clear Search</span>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" class="has-text-centered py-5">
          <div class="is-flex is-justify-content-center mb-4">
            <span class="icon is-large has-text-primary">
              <i class="bx bx-loader bx-spin bx-lg"></i>
            </span>
          </div>
          <p class="has-text-grey">Loading roles...</p>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state has-text-centered py-5">
          <div class="is-flex is-justify-content-center mb-4">
            <span class="icon is-large has-text-primary-light">
              <i class="bx bx-shield-x bx-lg"></i>
            </span>
          </div>
          <h4 class="title is-4 mb-3">No Roles Found</h4>
          <p class="subtitle is-6 has-text-grey mb-4">
            Start by creating your first role to manage system access control.
            <br>Roles define what users can see and do in the application.
          </p>
          <div>
            <permission-router-link to="/roles/create" class="button is-primary">
              <span class="icon">
                <i class="bx bx-plus"></i>
              </span>
              <span>Create First Role</span>
            </permission-router-link>
          </div>
        </div>
      </div>

      <!-- Card Footer with Pagination (if needed in the future) -->
      <footer class="card-footer" v-if="!loading && filteredRoles.length > 5">
        <div class="card-footer-item">
          <div class="level is-mobile" style="width: 100%">
            <div class="level-left">
              <div class="level-item">
                <p class="has-text-grey is-size-7">Showing {{ filteredRoles.length }} of {{ roles.length }} roles</p>
              </div>
            </div>
            <div class="level-right">
              <!-- Pagination placeholder for future implementation -->
            </div>
          </div>
        </div>
      </footer>
    </div>

    <!-- Delete Confirmation Modal -->

  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'RolesIndex',
  setup() {
    const router = useRouter()
    const route = useRoute()

    // Data
    const roles = ref([])
    const loading = ref(true)
    const searchQuery = ref('')
    const sortColumn = ref('name')
    const sortDirection = ref('asc')
    const successMessage = ref('')


    // Check for success message in query params
    if (route.query.success) {
      successMessage.value = route.query.success
      // Clear the success param from URL
      const query = { ...route.query }
      delete query.success
      router.replace({ query })

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        successMessage.value = ''
      }, 5000)
    }

    // Computed properties
    const filteredRoles = computed(() => {

      let result = roles.value;


      // Apply search filter if any
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(role => 
          role.name.toLowerCase().includes(query) ||
          (role.description && role.description.toLowerCase().includes(query))
        )
      }

      // Apply sorting
      result.sort((a, b) => {
        // Handle null values
        const aValue = a[sortColumn.value] ?? ''
        const bValue = b[sortColumn.value] ?? ''

        // Special case for dates
        if (sortColumn.value === 'created_at') {
          return sortDirection.value === 'asc' 
            ? new Date(aValue) - new Date(bValue)
            : new Date(bValue) - new Date(aValue)
        }

        // Default string comparison
        if (sortDirection.value === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })


      return result
    })

    // Methods
    const fetchRoles = async () => {
      loading.value = true
      try {
        const response = await axios.get('/api/roles')
        roles.value = response.data || []
      } catch (error) {
        console.error('Error fetching roles:', error)
      } finally {
        loading.value = false
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }

    const formatDateFull = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString()
    }

    const sortBy = (column) => {
      if (sortColumn.value === column) {
        // Toggle direction if clicking the same column
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
      } else {
        // Default to ascending order for new column
        sortColumn.value = column
        sortDirection.value = 'asc'
      }
    }




    // Stats calculation methods
    const getTotalPermissions = () => {
      return roles.value.reduce((total, role) => {
        return total + (role.permissions_count || 0)
      }, 0)
    }

    const getAdminRoles = () => {
      return roles.value.filter(role => {
        return role.name.toLowerCase().includes('sponsor')
      }).length
    }

    const getLatestRole = () => {
      if (!roles.value.length) return 'None'

      const sortedRoles = [...roles.value].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at)
      })

      return sortedRoles[0].name
    }

    // Watch for search query changes
    watch(searchQuery, () => {
      // Reset sort to default when searching
      if (searchQuery.value) {
        sortColumn.value = 'name'
        sortDirection.value = 'asc'
      }
    })

    onMounted(fetchRoles)

    return {
      roles,
      loading,
      searchQuery,
      sortColumn,
      sortDirection,
      successMessage,

      filteredRoles,
      formatDate,
      formatDateFull,
      fetchRoles,

      sortBy,
      getTotalPermissions,
      getAdminRoles,
      getLatestRole,
      router
    }
  }
}
</script>

<style lang="scss" scoped>
.roles-page {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

// Stats boxes styling
.stat-box {
  transition: all 0.3s ease;
  border-radius: 6px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  height: 100%;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }

  .title {
    margin-bottom: 0 !important;
  }

  .heading {
    text-transform: uppercase;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
}

// Card styling
.main-card {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

  .card-header {
    border-bottom: none;
  }

  .card-header-title {
    font-weight: 600;
  }
}

// Table styling
.roles-table {
  th {
    font-weight: 600;
    border-bottom: 2px solid #f0f0f0;

    &.is-sortable {
      cursor: pointer;
      user-select: none;

      &:hover {
        background-color: rgba(0, 0, 0, 0.03);
      }
    }
  }

  tr {
    transition: background-color 0.2s ease;
  }

  .role-name a {
    transition: color 0.2s ease;

    &:hover {
      color: var(--primary) !important;
      text-decoration: underline;
    }
  }

  .description-cell {
    max-width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// Buttons
.buttons {
  .button {
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
}

// Helper for text truncation
.truncate-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

// Success notification animation
.notification.fade-out {
  animation: fadeOut 0.5s ease-in-out 4.5s forwards;
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

// Empty state styling
.empty-state {
  max-width: 450px;
  margin: 0 auto;
  padding: 2rem 1rem;

  .icon {
    opacity: 0.8;
  }
}

// Modal styling
.modal-card {
  max-width: 500px;
  width: 90%;

  .modal-card-head {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  .modal-card-foot {
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }
}
</style>
